import { ScreenshotPreviewVM, ISelection } from './ScreenshotPreviewVM';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import sleep from 'cd-eft/utils/sleep';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';

import MapView from "@arcgis/core/views/MapView";
import SceneView from "@arcgis/core/views/SceneView";
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GraphicsLayerView from '@arcgis/core/views/layers/GraphicsLayerView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import Extent from '@arcgis/core/geometry/Extent';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as esriProjection from "@arcgis/core/geometry/projection";

export interface ILayerList {
    designElementTypeId: number,
    surfaceId?: number,
    designElementTypeIds?: number[],
    layers?: Layer[]
}

export interface IElement {
    designElementTypeId: number,
    designElementId: number
}

const ImageWidth = 1200;
const ImageHeight = 600;
const Aspect = ImageWidth / ImageHeight;

const Offset = 20;
const MaxScaleBar = 200;
const ScaleBarWidth = 6;
const ArrowRadius = 50;
const ArrowRatio = 5;
const NorthRatio = 1.25;

const FillBlack = {
    type: 'simple-fill', // autocasts as new esri.SimpleFillSymbol()
    color: 'black',
    style: 'solid',
    outline: {
        // autocasts as new esri.SimpleLineSymbol()
        color: 'black',
        width: 0.01,
    },
} as any;

const FillWhite = {
    type: 'simple-fill', // autocasts as new esri.SimpleFillSymbol()
    color: 'white',
    style: 'solid',
    outline: {
        // autocasts as new esri.SimpleLineSymbol()
        color: 'black',
        width: 0.01,
    },
} as any;

const ArrowCircleSymbol = {
    type: 'simple-line', // autocasts as new esri.SimpleLineSymbol()
    color: 'black',
    width: 1,
} as any;

const CommonTextSymbol: any = {
    color: 'black',
    haloColor: 'white',
    haloSize: '1px',
    font: { // autocast as Font
        size: 10,
        family: 'Arial Unicode MS',
        weight: 'normal'
    }
};

const NorthTextSymbol = {
    type: "text",  // autocasts as new TextSymbol()
    color: "black",
    haloColor: "white",
    haloSize: "1px",
    text: "N",
    horizontalAlignment: "center",
    verticalAlignment: "middle",
    font: {
        size: 18,
        family: "Arial Unicode MS",
        style: "italic",
        weight: "bold"
    }
} as any;

const imageOptions: ISelection[] = [
    {
        name: 'Plan View',
        value: 1,
        optionDisabled: false,
    },
    {
        name: 'Location View',
        value: 2,
        optionDisabled: false,
    },
    {
        name: 'Other View',
        value: 3,
        optionDisabled: false,
    }
]

const imageOptionsOtherOnly: ISelection[] = [
    {
        name: 'Other View',
        value: 3,
        optionDisabled: false,
    }
]

export class Screenshot {
    private readonly view: MapView | SceneView;
    private readonly isMapView: boolean;
    private readonly projectUuid: string;
    private readonly sessionUuid: string;
    private isTerrace: boolean = false;
    private layer: GraphicsLayer | undefined;
    private layerView: GraphicsLayerView | undefined;

    private preview: ScreenshotPreviewVM;
    private gisVm?: object;

    constructor(
        view: MapView | SceneView,
        projectUuid: string,
        sessionUuid: string,
        preview: ScreenshotPreviewVM,
        gisVm?: object
    ) {
        this.view = view;
        this.isMapView = (view instanceof MapView);
        this.projectUuid = projectUuid;
        this.sessionUuid = sessionUuid;
        this.preview = preview;
        this.gisVm = gisVm;
    }

    dispose(): void {
        this.disposeLayer();
    }

    public async takeScreenshot(layerList: ILayerList): Promise<void> {
        const mapCenter = this.view.center;
        const screenCenter = this.view.toScreen(mapCenter);
        const screenWidth = screenCenter?.x * 2;
        const screenHeight = screenCenter?.y * 2;
        let shotWidth = screenWidth;
        let shotHeight = screenHeight;
        let xMin = 0;
        let yMin = 0;
        let elements;

        if (screenWidth / screenHeight >= Aspect) {
            shotWidth = screenHeight * Aspect;
            xMin = (screenWidth - shotWidth) / 2;
        } else {
            shotHeight = screenWidth / Aspect;
            yMin = (screenHeight - shotHeight) / 2;
        }

        // create screenshot polygon
        if (this.gisVm === undefined) {
            if (this.isMapView && (layerList.designElementTypeId === 4)) {
                this.isTerrace = true;
            }
            let screenshotPolygon: Polygon | undefined = undefined;
            if (this.isMapView) {
                const p0 = this.view.toMap({ x: xMin, y: yMin });
                const p1 = this.view.toMap({ x: xMin + shotWidth, y: yMin });
                const p2 = this.view.toMap({ x: xMin + shotWidth, y: yMin + shotHeight });
                const p3 = this.view.toMap({ x: xMin, y: yMin + shotHeight });
                screenshotPolygon = new Polygon({
                    rings: [[[p0.x, p0.y], [p1.x, p1.y], [p2.x, p2.y], [p3.x, p3.y], [p0.x, p0.y]]],
                    spatialReference: this.view.spatialReference
                });
            }

            // build the list of elenents to attach the image to
            elements = await Screenshot.buildElementList(layerList, screenshotPolygon);
            // console.warn('elements');
            // console.warn(elements);
            if (elements.length < 1) {
                this.stopLoaders();
                await MessageCenter.addErrorFromCode('DesignElement_9');
                return;
            }
        }

        const area = {
            x: xMin,
            y: yMin,
            width: shotWidth,
            height: shotHeight
        };

        if (this.isMapView) {
            const ulMapCorner = this.view.toMap({ x: xMin, y: yMin });
            const urMapCorner = this.view.toMap({ x: xMin + ImageWidth, y: yMin });
            const dX = urMapCorner.x - ulMapCorner.x;
            const dY = urMapCorner.y - ulMapCorner.y;
            const distance = Math.sqrt(dX * dX + dY * dY);
            const rawScale = distance / ImageWidth;
            const scaleFactor = Math.cos(mapCenter.latitude * Math.PI / 180);
            const trueScale = rawScale * scaleFactor;
            this.drawElementsAndCapture(area, trueScale, scaleFactor, elements);
        } else {
            await this.captureScreenshot(this.view, area, imageOptionsOtherOnly, elements);
        }
    }

    /* istanbul ignore next: Unit Tests In Progress */
    public static async takeFullViewScreenshot(view: MapView, sessionUuid: string) {
        const shotWidth = view.width;
        const shotHeight = view.height;
        const mapCenter = view.center;
        const ulMapCorner = view.toMap({ x: 0, y: 0 });
        const urMapCorner = view.toMap({ x: shotWidth, y: 0 });

        let imageWidth: number;
        let imageHeight: number;
        if (shotWidth > shotHeight) {
            imageWidth = ImageWidth;
            imageHeight = ImageWidth * shotHeight / shotWidth;
        } else {
            imageHeight = ImageWidth;
            imageWidth = ImageWidth * shotWidth / shotHeight;
        }

        const dX = urMapCorner.x - ulMapCorner.x;
        const dY = urMapCorner.y - ulMapCorner.y;
        const distance = Math.sqrt(dX * dX + dY * dY);
        const rawScale = distance / imageWidth;

        const scaleFactor = Math.cos(mapCenter.latitude * Math.PI / 180);
        const trueScale = rawScale * scaleFactor;

        const area = {
            x: 0,
            y: 0,
            width: shotWidth,
            height: shotHeight
        };


        const drawLayer = new GraphicsLayer();
        let layerView: GraphicsLayerView | undefined;
        drawLayer.on('layerview-create', (e) => {
            layerView = e.layerView as GraphicsLayerView;
            layerView.when(async function () {
                await sleep(2000);
                try {
                    await Screenshot.captureFullView(view, area, imageWidth, imageHeight, sessionUuid);
                } catch {
                    MessageCenter.addWarningFromCode('Common_20');
                } finally {
                    view.map.remove(drawLayer);
                    drawLayer.destroy();
                }
            });
        });
        drawLayer.on('layerview-destroy', (e) => {
            layerView = undefined;
        });

        Screenshot.drawElements(view, drawLayer, area, trueScale, scaleFactor);

        view.map.add(drawLayer);
        await sleep(10000);
    }

    private static async captureFullView(
        view: MapView,
        area: any,
        imageWidth: number,
        imageHeight: number,
        sessionUuid: string
    ) {
        const screenshot = await view.takeScreenshot({
            width: imageWidth,
            height: imageHeight,
            area: area,
            format: 'png',
            ignorePadding: true
        });
        await ScreenshotPreviewVM.savePlanWithoutDialog(screenshot.dataUrl, sessionUuid);
    }

    private disposeLayer() {
        if (this.layer && this.view && this.view.map) {
            this.view.map.remove(this.layer);
            this.layer.destroy();
            this.layer = undefined;
        }
    }

    private drawElementsAndCapture(area: any, scale: number, scaleFactor: number, elements?: IElement[]) {
        this.disposeLayer();
        this.layer = new GraphicsLayer();
        const mapView = this.view;
        const thisRef = this;
        this.layer.on('layerview-create', (e) => {
            this.layerView = e.layerView as GraphicsLayerView;
            this.layerView.when(async function () {
                await sleep(2000);
                await thisRef.captureScreenshot(mapView, area, imageOptions, elements);
                thisRef.disposeLayer();
            });
        });
        this.layer.on('layerview-destroy', (e) => {
            this.layerView = undefined;
        });

        Screenshot.drawElements(this.view as MapView, this.layer, area, scale, scaleFactor);
        this.view.map.add(this.layer);
    }

    private async captureScreenshot(
        view: MapView | SceneView,
        area: any,
        saveOptions: ISelection[],
        elements?: IElement[]
    ) {
        let hasError = false;
        try {
            const screenshot = await view.takeScreenshot({
                width: ImageWidth,
                height: ImageHeight,
                area: area,
                format: 'png',
                ignorePadding: true
            });

            this.stopLoaders();
            await this.preview.showDialog(
                screenshot.dataUrl,
                this.projectUuid,
                this.sessionUuid,
                this.isTerrace,
                saveOptions,
                elements
            );
        } catch (e) {
            hasError = true;
        } finally {
            this.stopLoaders();
            if (hasError) {
                await MessageCenter.addErrorFromCode('Common_20');
            }
        }
    }

    private stopLoaders() {
        if (this.gisVm) {
            this.gisVm['loadingResults'] = false;
        } else {
            DesignContainerVM.screenshotLoading = false;
        }
    }

    private static drawElements(view: MapView, layer: GraphicsLayer, area: any, scale: number, scaleFactor: number) {
        const rotation = view.rotation / 180 * Math.PI;
        const sin = Math.sin(rotation);
        const cos = Math.cos(rotation);

        Screenshot.drawScalebar(view, layer, area, scale, scaleFactor, cos, sin);
        Screenshot.drawNorthArrow(view, layer!, area, scale, scaleFactor, cos, sin);
    }

    private static drawScalebar(
        view: MapView,
        layer: GraphicsLayer,
        area: any,
        scale: number,
        scaleFactor: number,
        cos: number,
        sin: number
    ) {
        const trueOffset = Offset * scale / scaleFactor;
        const trueWidth = ScaleBarWidth * scale / scaleFactor;
        const halfWidth = trueWidth / 2;
        const p0 = view.toMap({ x: area.x, y: area.y + area.height });
        const scaleBarSize = Screenshot.getScaleBarSize(scale);
        const label = (scaleBarSize > 4000 ? `${scaleBarSize / 1000} km` : `${scaleBarSize} m`);
        const viewSize = scaleBarSize / scaleFactor;
        const quarterSize = viewSize / 4;

        const bars: Graphic[] = [];
        const xStart = p0.x + trueOffset * (cos - sin);
        const y0 = p0.y + trueOffset * (cos + sin);
        let step = quarterSize / 5;
        for (let i = 0; i < 5; i++) {
            const deltaX0 = quarterSize * i / 5;
            const deltaX1 = deltaX0 + step;
            const p1 = [xStart + deltaX0 * cos - trueWidth * sin, y0 + deltaX0 * sin + trueWidth * cos];
            const p2 = [xStart + deltaX1 * cos - trueWidth * sin, y0 + deltaX1 * sin + trueWidth * cos];
            const p3 = [xStart + deltaX1 * cos - halfWidth * sin, y0 + deltaX1 * sin + halfWidth * cos];
            const p4 = [xStart + deltaX0 * cos - halfWidth * sin, y0 + deltaX0 * sin + halfWidth * cos];
            let geometry = new Polygon({
                rings: [[p1, p2, p3, p4, p1]],
                spatialReference: view.spatialReference
            });
            bars.push(new Graphic({
                geometry: geometry,
                symbol: ((i === 1) || (i === 3) ? FillWhite : FillBlack)
            }));
            const p5 = [xStart + deltaX1 * cos, y0 + deltaX1 * sin];
            const p6 = [xStart + deltaX0 * cos, y0 + deltaX0 * sin];
            geometry = new Polygon({
                rings: [[p4, p3, p5, p6, p4]],
                spatialReference: view.spatialReference
            });
            bars.push(new Graphic({
                geometry: geometry,
                symbol: ((i === 1) || (i === 3) ? FillBlack : FillWhite)
            }));
        }

        step = viewSize / 4;
        for (let i = 1; i < 4; i++) {
            const deltaX0 = viewSize * i / 4;
            const deltaX1 = deltaX0 + step;
            const p1 = [xStart + deltaX0 * cos - trueWidth * sin, y0 + deltaX0 * sin + trueWidth * cos];
            const p2 = [xStart + deltaX1 * cos - trueWidth * sin, y0 + deltaX1 * sin + trueWidth * cos];
            const p3 = [xStart + deltaX1 * cos - halfWidth * sin, y0 + deltaX1 * sin + halfWidth * cos];
            const p4 = [xStart + deltaX0 * cos - halfWidth * sin, y0 + deltaX0 * sin + halfWidth * cos];
            let geometry = new Polygon({
                rings: [[p1, p2, p3, p4, p1]],
                spatialReference: view.spatialReference
            });
            bars.push(new Graphic({
                geometry: geometry,
                symbol: ((i === 1) || (i === 3) ? FillWhite : FillBlack)
            }));
            const p5 = [xStart + deltaX1 * cos, y0 + deltaX1 * sin];
            const p6 = [xStart + deltaX0 * cos, y0 + deltaX0 * sin];
            geometry = new Polygon({
                rings: [[p4, p3, p5, p6, p4]],
                spatialReference: view.spatialReference
            });
            bars.push(new Graphic({
                geometry: geometry,
                symbol: ((i === 1) || (i === 3) ? FillBlack : FillWhite)
            }));
        }

        const text0 = new Point({
            x: xStart - (trueWidth + halfWidth) * sin,
            y: y0 + (trueWidth + halfWidth) * cos,
            spatialReference: view.spatialReference
        });
        let textSymbol = new TextSymbol({
            ...CommonTextSymbol,
            text: '0'
        });
        bars.push(new Graphic({
            geometry: text0,
            symbol: textSymbol
        }));

        const text1 = new Point({
            x: xStart + viewSize * cos - (trueWidth + halfWidth) * sin,
            y: y0 + viewSize * sin + (trueWidth + halfWidth) * cos,
            spatialReference: view.spatialReference
        });
        textSymbol = new TextSymbol({
            ...CommonTextSymbol,
            text: label
        });
        bars.push(new Graphic({
            geometry: text1,
            symbol: textSymbol
        }));

        layer.graphics.addMany(bars);
    }

    private static drawNorthArrow(
        view: MapView,
        layer: GraphicsLayer,
        area: any,
        scale: number,
        scaleFactor: number,
        cos: number,
        sin: number
    ) {
        const trueOffset = (Offset * 1.1 + ArrowRadius) * scale / scaleFactor;
        const trueRadius = ArrowRadius * scale / scaleFactor;
        const trueWidth = trueRadius / ArrowRatio;
        const p0 = view.toMap({ x: area.x, y: area.y });
        const centerX = p0.x + trueOffset * (cos + sin);
        const centerY = p0.y + trueOffset * (sin - cos);

        const arrow: Graphic[] = [];
        const circle: number[][] = [];
        for (let i = 0; i <= 40; i++) {
            const angle = Math.PI * i / 20;
            circle.push([centerX + trueRadius * Math.cos(angle), centerY + trueRadius * Math.sin(angle)]);
        }
        const circleGeom = new Polyline({
            paths: [circle],
            spatialReference: view.spatialReference
        });
        arrow.push(new Graphic({ geometry: circleGeom, symbol: ArrowCircleSymbol }));

        const arrowNGeom = new Polygon({
            rings: [[[centerX, centerY + trueRadius],
            [centerX + trueWidth, centerY],
            [centerX - trueWidth, centerY],
            [centerX, centerY + trueRadius]]],
            spatialReference: view.spatialReference
        });
        arrow.push(new Graphic({ geometry: arrowNGeom, symbol: FillBlack }));

        const arrowSGeom = new Polygon({
            rings: [[[centerX, centerY - trueRadius],
            [centerX - trueWidth, centerY],
            [centerX + trueWidth, centerY],
            [centerX, centerY - trueRadius]]],
            spatialReference: view.spatialReference
        });
        arrow.push(new Graphic({ geometry: arrowSGeom, symbol: FillWhite }));

        const northGeom = new Point({
            x: centerX,
            y: centerY + trueRadius * NorthRatio,
            spatialReference: view.spatialReference
        });
        arrow.push(new Graphic({ geometry: northGeom, symbol: NorthTextSymbol }));

        layer.graphics.addMany(arrow);
    }

    private static getScaleBarSize(scale: number): number {
        let base = 1;
        let result = base;

        for (; ;) {
            for (let i = 0; i < 3; i++) {
                const size = base * Math.pow(2, i);
                if (size / scale > MaxScaleBar) {
                    return result;
                }
                result = size;
            }
            base *= 10;
        }
    }

    private static async buildElementList(layers: ILayerList, filter?: Polygon): Promise<IElement[]> {
        if (layers.designElementTypeId === 2) {
            return [{ designElementTypeId: 2, designElementId: layers.surfaceId! }];
        }

        const result: IElement[] = [];
        for (let i = 0; i < layers.layers!.length; i++) {
            const layer = layers.layers![i];
            if (layer && ((filter === undefined) || await Screenshot.dataOverlaps(layer, filter))) {
                let idString = '';
                if (layers.designElementTypeId === 5) {
                    idString = layer.id.replace('alignment', '');
                } else if (layers.designElementTypeId === 7) {
                    idString = layer.id.replace('Waterway', '');
                } else if (layers.designElementTypeId === 4) {
                    idString = layer.id.replace('Terrace', '');
                } else {
                    idString = layer.id;
                }
                const id = Number(idString);
                if (!isNaN(id)) {
                    if (layers.designElementTypeId === 11) {
                        const typeId = layers.designElementTypeIds![i];
                        if (typeId) {
                            result.push({ designElementTypeId: typeId, designElementId: id });
                        }
                    } else {
                        result.push({ designElementTypeId: layers.designElementTypeId, designElementId: id });
                    }
                }
            }
        }
        return result;
    }

    private static async dataOverlaps(layer: Layer, filter: Polygon): Promise<boolean> {
        if (layer.type === 'group') {
            return await Screenshot.dataOverlapsGroup(layer as GroupLayer, filter);
        } else if ((layer.type === 'graphics') || (layer.type === 'feature')) {
            return await Screenshot.dataOverlapsFeatures(layer, filter);
        }
        return false;
    }

    private static async dataOverlapsGroup(layer: GroupLayer, filter: Polygon): Promise<boolean> {
        for (const sublayer of layer.layers.toArray().filter(
            l => { return ((l.type === 'graphics') || (l.type === 'feature')); }
        )) {
            const result = await Screenshot.dataOverlapsFeatures(sublayer, filter);
            if (result) return result;
        }
        for (const sublayer of layer.layers.toArray().filter(l => { return (l.type === 'group'); })) {
            const result = await Screenshot.dataOverlapsGroup(sublayer as GroupLayer, filter);
            if (result) return result;
        }
        return false;
    }

    private static async dataOverlapsFeatures(layer: Layer, filter: Polygon): Promise<boolean> {
        let extent: Extent | undefined = undefined;
        if ((layer.type === 'graphics') || (layer.type === 'feature')) {
            for (const g of (layer.type === 'graphics' ? (layer as GraphicsLayer).graphics : (layer as FeatureLayer).source)) {
                if (g.geometry.extent) {
                    if (extent === undefined) {
                        extent = g.geometry.extent.clone();
                    } else {
                        extent = extent.union(g.geometry.extent);
                    }
                }
            }
        } else {
            return false;
        }

        if (extent === undefined) {
            return false;
        }

        const extentPolygon = Polygon.fromExtent(extent);
        if (!esriProjection.isLoaded()) {
            await esriProjection.load();
        }
        const extentPolygonProjected = esriProjection.project(extentPolygon, filter.spatialReference) as Polygon;
        return geometryEngine.intersects(extentPolygonProjected, filter);
    }
}
